import axios from "axios";

import sessionAPI from "@/features/session/sessionAPI";
import appLocalStorage from "@/features/session/appLocalStorage";

const authorizedAPIInstance = axios.create({
  baseURL: import.meta.env.VITE_REACT_APP_BASE_URL,
  responseType: "json",
});

authorizedAPIInstance.interceptors.request.use(
  async (requestConfig) => {
    if (!requestConfig.headers) {
      requestConfig.headers = new axios.AxiosHeaders();
    }
    requestConfig.headers["Authorization"] =
      appLocalStorage.getAuthToken() || "";
    return requestConfig;
  },
  (error) => {
    Promise.reject(error);
  },
);

authorizedAPIInstance.interceptors.request.use(
  async (requestConfig) => {
    requestConfig.params = {
      plant: appLocalStorage.getProductionUnit(),
      ...requestConfig.params,
    };
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const redirectToLogin = () => {
  const plant = appLocalStorage.getProductionUnit();
  const path = plant ? `/login` : "/";
  appLocalStorage.clearLocalStorage();
  window.location.href = path;
};

export const setupAPIInstanceInterceptor = () => {
  authorizedAPIInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const tokenData = appLocalStorage.getTokenData();

        if (!tokenData?.refresh_token) {
          redirectToLogin();
          return Promise.reject(error);
        }

        try {
          const tokens = await sessionAPI.refreshAccessToken(
            tokenData.refresh_token,
          );
          if (tokens.status !== 200) {
            redirectToLogin();
            return await Promise.reject(error);
          }
          // Cognito does not return a new refresh_token in the refresh response,
          // so we must preserve the original one — otherwise the next refresh
          // call sends an undefined token and the server returns 500.
          const newTokenData = {
            ...tokens.data,
            refresh_token: tokens.data.refresh_token || tokenData.refresh_token,
          };
          appLocalStorage.setTokenData(newTokenData);
          // Update the header on authorizedAPIInstance, not the global axios instance,
          // so the retried request picks up the new id_token correctly.
          authorizedAPIInstance.defaults.headers.common.Authorization =
            appLocalStorage.getAuthToken() || "";
          return await authorizedAPIInstance(originalRequest);
        } catch (err) {
          redirectToLogin();
          return Promise.reject(err);
        }
      }
      return Promise.reject(error);
    },
  );
};

export default authorizedAPIInstance;
